/**
 * API Endpoints Configuration
 * Centralized location for all API endpoints - eComm Notify (UMS)
 */

export const ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
    CHANGE_PASSWORD: '/auth/changePassword',
    CHANGE_PASSWORD_FIRST_TIME: '/auth/changePasswordFirstTime',
  },

  // Organization (Master Data)
  ORGANIZATION: {
    GET_CURRENT: '/organization/current',
    GET_ALL: '/organization',
    GET_BY_ID: (id) => `/organization/${id}`,
    CREATE: '/organization',
    UPDATE: (id) => `/organization/${id}`,
    DELETE: (id) => `/organization/${id}`,
  },

  // Branch
  BRANCH: {
    GET_CURRENT: '/branch/current',
    GET_ALL: '/branch',
    GET_BY_ID: (id) => `/branch/${id}`,
    CREATE: '/branch',
    UPDATE: (id) => `/branch/${id}`,
    DELETE: (id) => `/branch/${id}`,
  },

  // File Uploads
  UPLOADS: {
    LOGO: '/api/uploads/logo',
    GET_LOGO: (branchId, filename) => `/api/uploads/logos/${branchId}/${filename}`,
    DELETE_LOGO: '/api/uploads/logo',
    CONFIG: '/api/uploads/config',
  },

  // Users
  USERS: {
    BASE: '/user',
    GET_ALL: '/user',
    GET_BY_ID: (id) => `/user/${id}`,
    FILTER: '/user/filter',
    CREATE: '/user',
    UPDATE: (id) => `/user/${id}`,
    DELETE: (id) => `/user/${id}`,
    UPDATE_PREFERENCE: (id) => `/user/${id}/preference`,
    BY_ROLE: (role) => `/user/by-role/${role}`,
    BULK_UPLOAD: '/user/bulk-upload',
  },

  // Roles
  ROLES: {
    GET_ALL: "/role",
    GET_BY_ID: (id) => `/role/${id}`,
    CREATE: "/role",
    UPDATE: (id) => `/role/${id}`,
    DELETE: (id) => `/role/${id}`,
  },

  // Permissions
  PERMISSIONS: {
    GET_ALL: "/api/permissions",
  },

  // Notifications
  NOTIFICATION: {
    BASE: "/api/admin/notification/settings",
    GET_ALL: "/api/admin/notification/settings",
    UPDATE: (id) => `/api/admin/notification/settings/${id}`,
  },

  // Tenant Management & Onboarding
  TENANT: {
    SETUP: {
      CREATE_ORG: '/api/platform/setup/organizations',
      GET_ALL_ORGS: '/api/platform/setup/organizations',
      REGISTER_DB: '/api/platform/setup/databases',
      SYNC_ORG: (orgId, branchId) => `/api/platform/setup/org/${orgId}/branch/${branchId}`,
      CREATE_BRANCH: '/api/platform/setup/branch',
      SYNC_BRANCHES: (orgId) => `/api/platform/setup/organizations/${orgId}/sync-branches`,
      COMPLETE_SETUP: '/api/platform/setup/complete-setup',
      CREATE_ADMIN_USER: (orgId) => `/api/platform/setup/organizations/${orgId}/admin-user`,
      RELOAD_DATASOURCES: '/api/platform/setup/databases/reload',
      VALIDATE_TOKEN: '/api/platform/setup/validate-token',
      STATUS: '/api/platform/setup/branches/status',
    },
    MIGRATIONS: {
      RUN_ALL: '/api/platform/migrations/run-all',
      RUN_FOR_ORG: (orgId, branchId) => `/api/platform/migrations/org/${orgId}/branch/${branchId}`,
      STATUS: '/api/platform/migrations/status',
    },
    SUPER_ADMIN: {
      USERS: {
        GET_ALL: '/api/platform/setup/users',
        UPDATE_STATUS: (id) => `/api/platform/setup/users/${id}/status`,
      }
    }
  },

  // Sellers (Business entities)
  SELLERS: {
    GET_ALL: '/api/sellers',
    GET_BY_ID: (id) => `/api/sellers/${id}`,
    CREATE: '/api/sellers',
    UPDATE: (id) => `/api/sellers/${id}`,
    DELETE: (id) => `/api/sellers/${id}`,
    UPDATE_STATUS: (id) => `/api/sellers/${id}/status`,
    ASSIGNED_USERS: (id) => `/api/sellers/${id}/assigned-users`,
    ASSIGN_USERS: (id) => `/api/sellers/${id}/assign-users`,
    REMOVE_USERS: (id) => `/api/sellers/${id}/users`,
    BULK_UPLOAD: '/api/sellers/bulk-upload',
    EXPORT: '/api/sellers/export',
    ACCOUNT_MANAGER_STATS: '/api/sellers/account-manager-stats',
    NOTIFICATION_COVERAGE: '/api/sellers/notification-coverage',
  },

  // Seller Accounts / Channel Connections
  SELLER_ACCOUNTS: {
    GET_ALL: '/api/seller-accounts',
    CONNECT: '/api/seller-accounts/connect',
    DISCONNECT: (id) => `/api/seller-accounts/${id}/disconnect`,
    RECONNECT: (id) => `/api/seller-accounts/${id}/reconnect`,
    DELETE: (id) => `/api/seller-accounts/${id}`,
    HEALTH_CHECK: (id) => `/api/seller-accounts/${id}/health`,
    MARKETPLACES: (id) => `/api/seller-accounts/${id}/marketplaces`,
  },

  // Notification Configs (per-seller)
  NOTIFICATION_CONFIGS: {
    GET_BY_SELLER: (sellerId) => `/api/sellers/${sellerId}/notification-configs`,
    GET_ONE: (sellerId, type) => `/api/sellers/${sellerId}/notification-configs/${type}`,
    SAVE: (sellerId, type) => `/api/sellers/${sellerId}/notification-configs/${type}`,
    DELETE: (sellerId, type) => `/api/sellers/${sellerId}/notification-configs/${type}`,
    TEST: (sellerId, type) => `/api/sellers/${sellerId}/notification-configs/${type}/test`,
    UPDATE_STATUS: (sellerId, type) => `/api/sellers/${sellerId}/notification-configs/${type}/status`,
  },

  // Notification Deliveries (activity / history)
  NOTIFICATION_DELIVERIES: {
    GET_ALL: '/api/notification-deliveries',
    GET_BY_ID: (id) => `/api/notification-deliveries/${id}`,
    GET_STATS: '/api/notification-deliveries/stats',
    RESEND: (id) => `/api/notification-deliveries/${id}/resend`,
  },

  // SP-API Reports (Daily Summary, Listing Status, Feedback, Hijack)
  REPORTS: {
    DAILY_SUMMARY: '/api/reports/daily-summary',
    LISTING_STATUS: '/api/reports/listing-status',
    FEEDBACK: '/api/reports/feedback',
    HIJACK_CHECK: '/api/reports/hijack-check',
  },

  // Orders
  ORDERS: {
    GET_ALL: '/api/orders',
    SYNC: '/api/orders/sync',
    GET_ITEMS: (orderId) => `/api/orders/${orderId}/items`,
  },

  // Settlement Reports (Reconciliation)
  SETTLEMENT_REPORTS: {
    LIST: '/api/settlement-reports',
    SYNC: '/api/settlement-reports/sync',
    GET_BY_ID: (id) => `/api/settlement-reports/${id}`,
    GET_RAW: (id) => `/api/settlement-reports/${id}/raw`,
    PARSE: (id) => `/api/settlement-reports/${id}/parse`,
    TRANSACTIONS: (id) => `/api/settlement-reports/${id}/transactions`,
    OVERVIEW: (id) => `/api/settlement-reports/${id}/overview`,
    ORDER_PNL: (id) => `/api/settlement-reports/${id}/order-pnl`,
    // Range-based (preferred — date-driven, aggregates across COD + Electronic settlements)
    RANGE_OVERVIEW:    '/api/settlement-reports/range/overview',
    RANGE_ORDER_PNL:   '/api/settlement-reports/range/order-pnl',
    RANGE_TRANSACTIONS:'/api/settlement-reports/range/transactions',
    RANGE_BUCKET_TXS:  '/api/settlement-reports/range/transactions/bucket',
    RANGE_SETTLEMENTS: '/api/settlement-reports/range/settlements',
    RANGE_CASHFLOW:    '/api/settlement-reports/range/cashflow',
    RANGE_SKU_PERF:    '/api/settlement-reports/range/sku-performance',
  },

  // Products
  PRODUCTS: {
    SYNC: '/api/products/sync',
    LIST: '/api/products',
  },

  // Pricing Calculator (Phase D)
  PRICING_CALC: {
    ESTIMATE:    '/api/pricing-calculator/estimate',
    PRODUCTS:    '/api/pricing-calculator/products',
    RATES:       '/api/pricing-calculator/rates',
    SP_API_FEES: '/api/pricing-calculator/sp-api-fees',
  },

  // Amazon OAuth
  AMAZON_OAUTH: {
    AUTHORIZE: '/api/seller-accounts/oauth/authorize',
    CALLBACK: '/api/seller-accounts/oauth/callback',
  },

  // Seller Feedbacks
  SELLER_FEEDBACKS: {
    GET_ALL: '/api/seller-feedbacks',
    SYNC: '/api/seller-feedbacks/sync',
  },

  // Hijack Alerts
  HIJACK_ALERTS: {
    GET_ALL: '/api/hijack-alerts',
  },

  // Sponsored Ad Performance (Amazon Advertising API)
  AD_PERFORMANCE: {
    CAMPAIGNS: '/api/ad-performance/campaigns',
    STATS: '/api/ad-performance/stats',
    KEYWORDS: '/api/ad-performance/keywords',
    SYNC: '/api/ad-performance/sync',
    OAUTH_AUTHORIZE: '/api/ad-performance/oauth/authorize',
    OAUTH_CALLBACK: '/api/ad-performance/oauth/callback',
    RUN_NOW: '/api/ad-performance/reports',
  },

  // Policy Warnings
  POLICY_WARNINGS: {
    GET_ALL: '/api/policy-warnings',
    SYNC: '/api/policy-warnings/sync',
  },

  // Review Requests (data from review_requests table)
  REVIEW_REQUESTS: {
    GET_ALL: '/api/review-requests',
  },

  // Feedback / Review Requests (Solicitations API actions)
  FEEDBACK_REQUESTS: {
    SYNC: '/api/feedback-requests/sync',
    SYNC_ALL: '/api/feedback-requests/sync-all',
    CHECK_ELIGIBILITY: '/api/feedback-requests/check-eligibility',
    CHECK_SINGLE: (orderId) => `/api/feedback-requests/orders/${orderId}/check`,
    SEND: (orderId) => `/api/feedback-requests/orders/${orderId}/send`,
    BULK_SEND: '/api/feedback-requests/bulk-send',
    GENERATE_NOW: '/api/feedback-requests/generate-now',
  },

  // Dashboard
  DASHBOARD: {
    SUMMARY: '/api/dashboard/summary',
  },

  // Scheduler
  SCHEDULER: {
    STATUS: '/api/scheduler/status',
    REFRESH: '/api/scheduler/refresh',
  },

  INTEGRATION: {
    WHATSAPP_PROVIDER: {
      GET_ALL:    '/integration/whatsapp-providers',
      CREATE:     '/integration/whatsapp-providers',
      UPDATE:     (id) => `/integration/whatsapp-providers/${id}`,
      DELETE:     (id) => `/integration/whatsapp-providers/${id}`,
      ACTIVATE:   (id) => `/integration/whatsapp-providers/${id}/activate`,
      DEACTIVATE: (id) => `/integration/whatsapp-providers/${id}/deactivate`,
    },
  },

  // Module Subscription
  MODULE_SUBSCRIPTION: {
    GET_BRANCH_MODULES: (branchId) => `/api/platform/setup/branch/${branchId}`,
    UPDATE_BRANCH_MODULES: (branchId) => `/api/platform/setup/branch/${branchId}`,
    DELETE_BRANCH_MODULE: (branchId, moduleCode) => `/api/platform/setup/branch/${branchId}/${moduleCode}`,
    GET_CURRENT_USER_MODULES: '/api/platform/setup/current-user-modules',
  },
}

export default ENDPOINTS;
